/**
 * Shared "refined tint" chip recipe for badges and tags.
 *
 * One tuning knob for every small colored chip in the app. The palette IS the
 * brand's existing tag colors (`--color-tags-*` in tailwind.css); maturity comes
 * from the implementation: the brand color as a soft background tint, inky
 * hue-matched text instead of generic gray, and a hairline border drawn from
 * the same ink. No new hues are invented.
 *
 * Badges (app/components/ui/badge.tsx) and tags (app/components/tags/TagChip.tsx)
 * both compose from these recipes so the two systems read as one family.
 *
 * NOTE: Class strings must stay literal in this file or Tailwind's scanner
 * purges them from the build (same constraint as app/utils/presetColors.ts).
 */

/** Shared chip anatomy: height, radius, hairline border, spacing, type. */
export const chipBase = `inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded border-[0.5px] px-1.5 align-middle text-label-xs`

// Ink (text) and border colors live in --chip-<hue>-ink/-border (src/styles/tailwind.css):
// the brand tag hues pulled down to reading darkness with moderated chroma in light mode,
// lifted to a light tint in dark mode so they clear 4.5:1 against the fill. Backgrounds are
// the actual brand colors at low alpha, so a tag still reads as "the blue one" without painting
// the row.
const hues = {
  gray: `bg-tags-gray/40 text-(--chip-gray-ink) border-(--chip-gray-border)`,
  silver: `bg-tags-silver/50 text-(--chip-silver-ink) border-(--chip-silver-border)`,
  red: `bg-tags-salmon/14 text-(--chip-red-ink) border-(--chip-red-border)`,
  warning: `bg-tags-red/12 text-(--chip-warning-ink) border-(--chip-warning-border)`,
  orange: `bg-tags-orange/16 text-(--chip-orange-ink) border-(--chip-orange-border)`,
  amber: `bg-tags-amber/22 text-(--chip-amber-ink) border-(--chip-amber-border)`,
  yellow: `bg-tags-yellow/25 text-(--chip-yellow-ink) border-(--chip-yellow-border)`,
  green: `bg-tags-green/16 text-(--chip-green-ink) border-(--chip-green-border)`,
  sky: `bg-[oklch(71%_0.13_225/0.15)] text-(--chip-sky-ink) border-(--chip-sky-border)`,
  blue: `bg-primary-alpha-8 text-(--chip-blue-ink) border-(--chip-blue-border)`,
  purple: `bg-tags-purple/15 text-(--chip-purple-ink) border-(--chip-purple-border)`,
  pink: `bg-tags-pink/15 text-(--chip-pink-ink) border-(--chip-pink-border)`,
  rose: `bg-tags-salmon/16 text-(--chip-rose-ink) border-(--chip-rose-border)`,
} as const

export type ChipHue = keyof typeof hues

export const hueClasses: Record<ChipHue, string> = hues

/** Stored TagColor class strings (`bg-tags-*`) are palette lookup keys, never applied as classes. */
const tagHues: Record<string, ChipHue> = {
  'bg-tags-silver': `silver`,
  'bg-tags-gray': `gray`,
  'bg-tags-salmon': `rose`,
  'bg-tags-orange': `orange`,
  'bg-tags-yellow': `yellow`,
  'bg-tags-green': `green`,
  'bg-tags-blue': `blue`,
  'bg-tags-purple': `purple`,
  'bg-tags-pink': `pink`,
  'bg-tags-red': `warning`,
  'bg-tags-amber': `amber`,
}

/** Display names for the stored tag color keys (shown in the picker and tags table). */
export const tagColorNames: Record<string, string> = {
  'bg-tags-silver': `Silver`,
  'bg-tags-gray': `Gray`,
  'bg-tags-salmon': `Red`,
  'bg-tags-orange': `Orange`,
  'bg-tags-yellow': `Yellow`,
  'bg-tags-green': `Green`,
  'bg-tags-blue': `Blue`,
  'bg-tags-purple': `Purple`,
  'bg-tags-pink': `Pink`,
  'bg-tags-red': `Warning`,
  'bg-tags-amber': `Amber`,
}

/** Recipe classes for a stored tag color key; unknown/legacy values fall back to silver. */
export function tagChipClasses(colorKey?: string | null): string {
  return hues[tagHues[colorKey ?? ``] ?? `silver`]
}

/** Solid swatch background for a stored tag color key (picker swatches, list dots).
 *  Swatches show the true brand color; the recipe only softens how chips wear it. */
export function tagSwatchClasses(colorKey?: string | null): string {
  return colorKey && tagHues[colorKey] ? colorKey : `bg-tags-silver`
}
